import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StudentRecord } from '@/mocks/students';
import { incidentLogs } from '@/mocks/preInspection';
import Badge from '@/components/base/Badge';
import CallDropdown from '@/components/feature/CallParentDropdown';
import IncidentTooltip from '@/components/feature/IncidentTooltip';

interface StudentTableProps {
  students: StudentRecord[];
  onRowClick?: (student: StudentRecord) => void;
  onStatusChange?: (student: StudentRecord, newStatus: string) => void;
}

export default function StudentTable({ students, onRowClick, onStatusChange }: StudentTableProps) {
  const { t } = useTranslation();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const confirmRef = useRef<HTMLDivElement>(null);
  const [hoveredIncidentStudentId, setHoveredIncidentStudentId] = useState<string | null>(null);
  const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);
  const incidentBtnRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const setIncidentBtnRef = useCallback((studentId: string) => (el: HTMLButtonElement | null) => {
    if (el) {
      incidentBtnRefs.current.set(studentId, el);
    } else {
      incidentBtnRefs.current.delete(studentId);
    }
  }, []);

  const showIncidentTooltip = useCallback((studentId: string) => {
    const btn = incidentBtnRefs.current.get(studentId);
    if (btn) {
      setTooltipRect(btn.getBoundingClientRect());
    }
    setHoveredIncidentStudentId(studentId);
  }, []);

  const hideIncidentTooltip = useCallback(() => {
    setHoveredIncidentStudentId(null);
    setTooltipRect(null);
  }, []);

  // Build incident lookup: studentId → their incidents
  const incidentsByStudent: Record<string, typeof incidentLogs> = {};
  incidentLogs.forEach((inc) => {
    if (!incidentsByStudent[inc.studentId]) {
      incidentsByStudent[inc.studentId] = [];
    }
    incidentsByStudent[inc.studentId].push(inc);
  });

  const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'neutral' | 'error' }> = {
    enrolled: { label: t('students.enrolled'), variant: 'success' },
    pending:  { label: t('students.pending'), variant: 'warning' },
    draft:    { label: t('students.draft'), variant: 'neutral' },
    inactive: { label: t('students.inactive'), variant: 'error' },
  };

  useEffect(() => {
    if (!confirmingId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (confirmRef.current && !confirmRef.current.contains(e.target as Node)) {
        setConfirmingId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [confirmingId]);

  const handleConfirmReactivate = (student: StudentRecord) => {
    setConfirmingId(null);
    onStatusChange?.(student, 'enrolled');
  };

  if (students.length === 0) {
    return (
      <div className="bg-background-50 rounded-xl border border-background-200 p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
          <i className="ri-user-search-line text-2xl text-foreground-400" />
        </div>
        <p className="text-foreground-500 font-label text-sm">{t('students.noStudentsFound')}</p>
      </div>
    );
  }

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-background-200 bg-background-100">
              <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.studentId')}</th>
              <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.name')}</th>
              <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.class')}</th>
              <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.classTeacher')}</th>
              <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.dob')}</th>
              <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.fatherName')}</th>
              <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.motherName')}</th>
              <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.contactFather')}</th>
              <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.contactMother')}</th>
              <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.district')}</th>
              <th className="text-left px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.status')}</th>
              <th className="text-center px-3 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.alert')}</th>
              <th className="text-center px-3 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('students.call')}</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s, idx) => {
              const status = statusConfig[s.status] || statusConfig.draft;
              const isConfirming = confirmingId === s.id;
              return (
                <tr
                  key={s.id}
                  className={`border-b border-background-100 transition-colors hover:bg-background-100/60 cursor-pointer ${
                    idx % 2 === 1 ? 'bg-background-50/50' : ''
                  }`}
                  onClick={() => onRowClick?.(s)}
                >
                  <td className="px-4 py-3 text-sm text-foreground-600 font-mono text-xs whitespace-nowrap">{s.studentId}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-xs font-label font-semibold flex-shrink-0">
                        {s.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="text-sm font-label font-medium text-foreground-800 whitespace-nowrap">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-label font-medium text-foreground-600 bg-background-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                      {s.className}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">{s.classTeacher}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">
                    {new Date(s.dob).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">{s.fatherName}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">{s.motherName}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600 font-mono text-xs whitespace-nowrap">{s.contactNumberF}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600 font-mono text-xs whitespace-nowrap">{s.contactNumberM}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">{s.district}</td>
                  <td className="px-4 py-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant={status.variant} dot size="sm">{status.label}</Badge>
                        {s.status === 'inactive' && onStatusChange && (
                          <div className="relative" ref={isConfirming ? confirmRef : undefined}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmingId(isConfirming ? null : s.id);
                              }}
                              className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                                isConfirming
                                  ? 'bg-semantic-success text-white'
                                  : 'bg-semantic-success-bg text-semantic-success hover:bg-semantic-success/20'
                              }`}
                              title={isConfirming ? t('students.confirmReactivation') : t('students.reactivateStudent')}
                            >
                              <i className={`text-xs ${isConfirming ? 'ri-check-line' : 'ri-refresh-line'}`} />
                            </button>
                            {isConfirming && (
                              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 z-40 animate-[modalEnter_150ms_cubic-bezier(0.4,0,0.2,1)]">
                                <div className="bg-background-50 rounded-lg border border-background-200 shadow-floating px-3 py-2 flex items-center gap-2 whitespace-nowrap">
                                  <span className="text-xs font-label font-medium text-foreground-700">{t('students.reactivateConfirm', { name: s.name.split(' ')[0] })}</span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleConfirmReactivate(s);
                                    }}
                                    className="px-2 py-0.5 text-xs font-label font-semibold rounded bg-semantic-success text-white hover:bg-semantic-success/90 transition-colors cursor-pointer"
                                  >
                                    {t('common.yes')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setConfirmingId(null);
                                    }}
                                    className="px-2 py-0.5 text-xs font-label font-semibold rounded bg-background-200 text-foreground-600 hover:bg-background-300 transition-colors cursor-pointer"
                                  >
                                    {t('common.no')}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {s.status === 'inactive' && s.deactivatedAt && (
                        <p className="text-[10px] text-foreground-400 mt-1 font-label whitespace-nowrap">
                          {new Date(s.deactivatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </td>
                  {/* Incident / Alert Indicator */}
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-center relative">
                      {(() => {
                        const studentIncidents = incidentsByStudent[s.studentId];
                        if (!studentIncidents || studentIncidents.length === 0) {
                          return (
                            <div className="w-8 h-8 flex items-center justify-center">
                              <div className="w-1.5 h-1.5 rounded-full bg-foreground-200" />
                            </div>
                          );
                        }
                        return (
                          <>
                            <button
                              ref={setIncidentBtnRef(s.studentId)}
                              type="button"
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 transition-colors cursor-pointer relative"
                              onMouseEnter={() => showIncidentTooltip(s.studentId)}
                              onMouseLeave={hideIncidentTooltip}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (hoveredIncidentStudentId === s.studentId) {
                                  hideIncidentTooltip();
                                } else {
                                  showIncidentTooltip(s.studentId);
                                }
                              }}
                            >
                              <i className="ri-alert-fill text-red-500 text-base" />
                              {studentIncidents.length > 1 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full bg-red-600 text-white text-[9px] font-label font-bold">
                                  {studentIncidents.length}
                                </span>
                              )}
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex justify-center">
                      <CallDropdown contactF={s.contactNumberF} contactM={s.contactNumberM} fatherName={s.fatherName} motherName={s.motherName} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-background-200 bg-background-50">
        <p className="text-xs text-foreground-500 font-label">
          {t('common.showing')} <span className="font-semibold text-foreground-700">{students.length}</span> {students.length !== 1 ? t('common.students') : t('common.student')}
        </p>
        <div className="flex items-center gap-2">
          <button className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer" disabled>
            <i className="ri-arrow-left-s-line text-sm" />
          </button>
          <span className="text-xs font-label font-medium text-foreground-700 px-2">1</span>
          <button className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer" disabled>
            <i className="ri-arrow-right-s-line text-sm" />
          </button>
        </div>
      </div>

      {/* Incident Tooltip Portal */}
      <IncidentTooltip
        show={hoveredIncidentStudentId !== null}
        incidents={hoveredIncidentStudentId ? (incidentsByStudent[hoveredIncidentStudentId] || []) : []}
        triggerRect={tooltipRect}
        onMouseEnter={() => {
          if (hoveredIncidentStudentId) setHoveredIncidentStudentId(hoveredIncidentStudentId);
        }}
        onMouseLeave={hideIncidentTooltip}
      />
    </div>
  );
}